/**
 * Logic Tower tutorials, shown the first time a level with that id opens.
 * The first levels teach one control each; every chapter's first level
 * introduces its new idea. `focus` tells the scene (tower, hopper, cups,
 * levers, gutters, gears, lids, colour levers, hoppers) or the HUD (undo,
 * target, hint) what to highlight; "demo" keeps a sparkle on the column of
 * the next good drop until the player drops there.
 */
export type TowerTutorialFocus =
  | "tower"
  | "hopper"
  | "cups"
  | "demo"
  | "lever"
  | "gutter"
  | "undo"
  | "target"
  | "hint"
  | "gear"
  | "lid"
  | "keyed"
  | "hoppers";

export interface TowerTutorialStep {
  readonly text: string;
  readonly focus?: TowerTutorialFocus;
}

export interface TowerTutorial {
  readonly id: string;
  readonly steps: readonly TowerTutorialStep[];
}

export const TOWER_TUTORIALS: readonly TowerTutorial[] = [
  {
    id: "tower-drop",
    steps: [
      { text: "Chào mừng tới Tháp Trí Tuệ! Bác thợ đồng hồ cần bi màu để lên dây cót cho tháp, mình phụ bác một tay nha.", focus: "tower" },
      { text: "Bi nằm chờ trên đường ray. Kéo viên bi qua lại là thấy ngay đường nó sẽ lăn, buông tay là bi rơi.", focus: "hopper" },
      { text: "Bi rơi trúng chén cùng màu là được giữ lại. Chén nào cũng đầy là tháp reo chuông ăn mừng.", focus: "cups" },
      { text: "Thả viên đầu tiên vào cột có đốm sáng thử xem nào.", focus: "demo" },
    ],
  },
  {
    id: "tower-lever",
    steps: [
      { text: "Đây là cần lật. Bi lăn theo hướng cần đang nghiêng, lăn qua xong là cần bật sang phía bên kia.", focus: "lever" },
      { text: "Viên sau đi ngang đúng cái cần đó sẽ rẽ hướng ngược lại. Ngó cần trước khi thả là bí kíp đó nha.", focus: "lever" },
    ],
  },
  {
    id: "tower-return",
    steps: [
      { text: "Bi rơi xuống máng trống, vào chén khác màu hay chén đã đầy thì leo thang máy về cuối hàng chờ.", focus: "gutter" },
      { text: "Không mất viên nào hết, chỉ tốn một lượt thả. Cố tình gửi bi đi vòng để lật cần lại là nước đi cao tay đó.", focus: "lever" },
      { text: "Lỡ tay thì hoàn tác thoải mái, không tốn xu nào. Ba sao tính theo số lượt thả còn giữ lại cuối cùng.", focus: "undo" },
    ],
  },
  {
    id: "tower-hint",
    steps: [
      { text: "Số mục tiêu nằm trên đầu nè. Xong màn gọn trong số đó là rinh trọn ba sao.", focus: "target" },
      { text: "Bí quá thì chạm bóng đèn nha. Mỗi màn có 2 lượt gợi ý miễn phí, gợi ý chỉ đúng một lượt thả tiếp theo thôi.", focus: "hint" },
    ],
  },
  {
    id: "tower-levers",
    steps: [
      { text: "Chương Cần lật nè! Cần nhiều hơn, bi cũng nhiều hơn. Trước khi thả, ngó xem viên này lật cần nào rồi viên sau sẽ đi đâu.", focus: "lever" },
      { text: "Gửi một viên đi vòng thang máy đúng lúc để chỉnh cần lại thường là đường gọn nhất đó nha.", focus: "gutter" },
    ],
  },
  {
    id: "tower-colors",
    steps: [{ text: "Giờ có ba màu bi rồi. Viên nào cũng có ký hiệu riêng, nhìn ký hiệu là khỏi lo nhầm màu.", focus: "cups" }],
  },
  {
    id: "tower-gear",
    steps: [{ text: "Hai cần chung một bánh răng là cặp song sinh. Một cần lật thì cần kia lật theo, dù bi chẳng chạm tới nó.", focus: "gear" }],
  },
  {
    id: "tower-lid",
    steps: [{ text: "Chén có nắp đồng đóng chặt cho tới khi các chén cùng màu viên ngọc trên nắp được làm đầy. Tính xem màu nào đi trước nha.", focus: "lid" }],
  },
  {
    id: "tower-keyed",
    steps: [{ text: "Cần có núm màu khó tính lắm. Chỉ bi cùng màu mới làm nó lật, bi màu khác lăn qua thì nó đứng im re.", focus: "keyed" }],
  },
  {
    id: "tower-hoppers",
    steps: [{ text: "Màn này có hai ống chờ. Chạm ống nào thì viên đầu của ống đó lên đường ray, rồi chọn cột thả như thường.", focus: "hoppers" }],
  },
  {
    id: "tower-gear-lid",
    steps: [{ text: "Bánh răng gặp nắp đồng! Lật một cần là cần song sinh lật theo, mà chén có nắp còn phải chờ màu khác đầy trước. Tính thứ tự màu trước rồi hẵng thả nha.", focus: "lid" }],
  },
  {
    id: "tower-keyed-hoppers",
    steps: [{ text: "Cần màu gặp hai ống chờ! Chọn viên từ ống nào cũng là một nước đi, vì chỉ bi đúng màu mới làm cần màu lật.", focus: "hoppers" }],
  },
  {
    id: "tower-finale",
    steps: [{ text: "Tòa tháp kết có đủ bánh răng, nắp đồng lẫn cần màu. Cứ thong thả mà tính, không ai hối đâu.", focus: "tower" }],
  },
];

export const TOWER_TUTORIAL_BY_ID: ReadonlyMap<string, TowerTutorial> = new Map(TOWER_TUTORIALS.map((t) => [t.id, t]));
